import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import AgencyAnalyticsLandingHelmet from 'common/helmets/AgencyAnalyticsLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import AgencyAnalyticsWordmark from 'img/landing/agencyanalytics-wordmark.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const AgencyAnalyticsLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed AgencyAnalytics Case Study');
  }, [trackEvent]);

  return (
    <div className="landingAgencyAnalytics landingCustomersPage">
      <AgencyAnalyticsLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How AgencyAnalytics uses Canny to earn more revenue and avoid churn" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/iJgM-O8iHvE"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              AgencyAnalytics is a reporting and dashboarding tool for digital marketing agencies.
              It helps marketers collect all their ad data and analyze it in one central place.
              Right now, the company works with 5,600+ marketing agencies and has almost 100 people
              on their&nbsp;team.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              Jay Patel is a product manager for the integration team at AgencyAnalytics. His team
              in particular is laser-focused on the voice of the customer. The team quickly realized
              that their feedback volume was too much for an Excel spreadsheet to handle. So, they
              started looking for a better&nbsp;solution.
            </p>
            <p className="quote">
              &ldquo;You think you might have just five or ten requests and a spreadsheet can handle
              them. But then you realize that there are different cohorts of customers with various
              use cases and requirements. So now the spreadsheet is getting longer and longer, and
              trying to make sense of it is just too&nbsp;much.&rdquo;
            </p>
            <p>
              Trying to keep track of all those requests while keeping the customers happy was
              becoming a big challenge. And using spreadsheets to manage it just
              wasn’t&nbsp;scalable.
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Canny became the perfect solution for AgencyAnalytics. It helps their teams really pay
              attention to their customers and keep their feedback&nbsp;organized.
            </p>
            <p className="quote">
              &ldquo;AgencyAnalytics strives for simplicity. And we can tell that Canny does too. It
              doesn’t overwhelm you with features. The design is clean and simple too, and it helps
              us do exactly what we&nbsp;need.&rdquo;
            </p>
            <p>
              AgencyAnalytics uses several boards in Canny to separate different ideas. This helps
              their users too – they log in to Canny and see a board that discusses only
              relevant&nbsp;features.
            </p>
            <p>
              AgencyAnalytics even has a dedicated person that manages each community. The team is
              also using tags, the Intercom integration, and the ClickUp&nbsp;integration.
            </p>
            <p>
              Several teams at AgencyAnalytics use Canny to stay organized: sales, customer support,
              customer success, and even engineering. Once they receive feedback, the product,
              design, and engineering teams get involved. They all assess each feature request in
              detail, as well&nbsp;as:
            </p>
            <ul>
              <li>Each use case</li>
              <li>The potential MRR</li>
              <li>The churn risk</li>
            </ul>
            <p>The sales team at AgencyAnalytics in particular really enjoys using&nbsp;Canny.</p>
            <p className="quote">
              &ldquo;Without leaving Hubspot, our sales team can communicate what the potential
              users want. Canny helps us not only keep the current MRR and make our current users
              happy, but also opens up opportunities for more&nbsp;revenue.&rdquo;
            </p>
            <p>
              Engineers and designers appreciate not needing to contact customers for details on
              their feedback. They can see customer requests and the conversations with them right
              in Canny. If they need clarification, they can also engage with the customers. That
              open flow of communication means a lot to AgencyAnalytics. Plus, it helps with their
              customer retention&nbsp;too!
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              It's important for AgencyAnalytics that they build features requested by their
              existing customers. On average for every month in 2022, AgencyAnalytics built
              requested features that were attributed to $120k of existing MRR.
            </p>
            <p className="quote">
              &ldquo;Canny has played a huge role in our understanding of what our customers need.
              What’s more, it’s now easy to see what they need right now, and what they need in the
              long term. Thanks to Canny, we find the issues that are most requested, avoid churn,
              and generate more&nbsp;revenue.&rdquo;
            </p>
            <p>
              The bottom line, according to Jay, is this: your customers will talk about you anyway.
              So why not control that conversation? If you can address their concerns, offer
              solutions, and deliver what they’re asking for, why wouldn’t you? All of that is
              possible with&nbsp;Canny.
            </p>
            <p className="quote">
              &ldquo;You can also keep your customers in the loop about your progress. They’ll feel
              like they’re in the center of your product and will stay with&nbsp;you.&rdquo;
            </p>
            <p>
              AgencyAnalytics strives to maintain their platform’s simplicity, serve their
              customers, and ultimately build a better product. This aligns so well with our goals
              at Canny, and we’re excited to continue the journey together
              with&nbsp;AgencyAnalytics.
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="agency analytics wordmark"
              className="agencyanalyticsWordmark"
              src={AgencyAnalyticsWordmark}
            />
            AgencyAnalytics is the no-code reporting software for marketing&nbsp;agencies.
            <a href="https://agencyanalytics.com/" rel="noopener" target="_blank">
              agencyanalytics.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>90</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2010</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2020</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>21,723+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};
export default hot(AgencyAnalyticsLanding);
